/**
 * insertionHints — context-aware recommendations for the Canvas Task Picker.
 *
 * Spec: agentlets-production/CANVAS_PICKER_INLINE_DESIGN.md §4.
 *
 * Pure function over (target, canvas). No React, no fetch, no I/O. Returns
 * a scored list of node types to surface in the picker's "Recommended here"
 * section. Each recommendation carries a `reason` string that the picker
 * displays under the row's `why?` chip.
 *
 * Why hand-coded instead of ML:
 *   • Explainability — every recommendation has a one-sentence rationale a
 *     compliance reviewer can read.
 *   • Predictability — the same insertion target always yields the same
 *     suggestions; no per-tenant model drift.
 *   • Cost — runs in microseconds, never leaves the browser, no API spend.
 *   • Auditability — security can grep the rules to confirm nothing weird
 *     is recommended in regulated workflows.
 *
 * Caps: keep the rule set under ~40. At that size we revisit the embeddings
 * question described in the design doc §3 (open question 3).
 *
 * @typedef {Object} InsertionTarget
 * @property {'edge'|'branch'|'after-node'|'palette'} kind
 * @property {string} [connectionId]
 * @property {string} [branchEdgeId]
 * @property {string} [switchNodeId]
 * @property {string} [branchLabel]
 * @property {string} [nodeId]
 *
 * @typedef {Object} CanvasView
 * @property {object[]} nodes        — read-only node array
 * @property {object[]} connections  — read-only connection array
 *
 * @typedef {Object} Hint
 * @property {string} type    — node type id to suggest
 * @property {number} score   — 0-100; only candidates >= 30 are surfaced
 * @property {string} reason  — human-readable rationale (rendered as the
 *                              "why?" chip body)
 */

const BRANCH_TYPES = new Set(['switch', 'condition', 'fork', 'fork_join_dynamic']);
const TERMINAL_TYPES = new Set(['end', 'terminate']);

/** Helpers — all pure, all tolerant of missing fields. */

function findNode(canvas, id) {
  return (canvas?.nodes || []).find((n) => n && n.id === id) || null;
}
function findConn(canvas, id) {
  return (canvas?.connections || []).find((c) => c && c.id === id) || null;
}
function nodeOnEdge(canvas, connectionId, end /* 'from' | 'to' */) {
  const conn = findConn(canvas, connectionId);
  if (!conn) return null;
  return findNode(canvas, end === 'from' ? conn.from : conn.to);
}
function outgoingEdges(canvas, nodeId) {
  return (canvas?.connections || []).filter((c) => c && c.from === nodeId);
}

/**
 * True iff the branch edge identified by `connectionId` ultimately leads to
 * neither an `end` nor a `terminate` node — i.e. the branch is "dangling"
 * from a workflow-validity perspective.
 *
 * Worst-case linear in connections; bounded by a 200-node visited cap so a
 * malformed cycle can never lock the picker.
 */
function branchHasNoExit(canvas, connectionId) {
  const conn = findConn(canvas, connectionId);
  if (!conn) return false;
  const visited = new Set();
  const stack = [conn.to];
  while (stack.length > 0 && visited.size < 200) {
    const id = stack.pop();
    if (visited.has(id)) continue;
    visited.add(id);
    const n = findNode(canvas, id);
    if (!n) continue;
    if (TERMINAL_TYPES.has(n.type)) return false;
    for (const out of outgoingEdges(canvas, id)) stack.push(out.to);
  }
  return true;
}

/** Heuristic: does this connector_task look like it WRITES to an external
 *  system (creates / sends / posts), as opposed to read-only enrichment? */
function isWritingConnectorTask(node) {
  if (!node || node.type !== 'connector_task') return false;
  const action = (node.config?.action || '').toLowerCase();
  if (!action) return false;
  return /(create|post|send|update|put|delete|push|publish|notify|quarantine|block|set|put|modify)/.test(action);
}

/** True iff the canvas only has the START node + maybe disconnected. The
 *  picker uses this to bias suggestions on a fresh playbook. */
function isFirstEdgeAfterStart(canvas, target) {
  if (target.kind !== 'edge' && target.kind !== 'after-node') return false;
  const fromNode =
    target.kind === 'edge' ? nodeOnEdge(canvas, target.connectionId, 'from')
                            : findNode(canvas, target.nodeId);
  return !!fromNode && (fromNode.type === 'manual_start' || fromNode.type === 'start');
}

// =========================================================================
// Rules — each is a pure (target, canvas) → Hint | null. Order doesn't
// matter; the caller takes the top N by score.
// =========================================================================

const rules = [
  // 1. After any LLM task, recommend policy_rule_evaluation.
  //    RC-AIPrompt-PhantomTypes-Round2 — was 'output_validator', which
  //    has no NODE_TYPES entry and no BE handler (legacy stub from an
  //    unbuilt feature). policy_rule_evaluation is the canonical
  //    rule-based output check the runtime actually supports.
  (t, c) => {
    if (t.kind !== 'edge') return null;
    const from = nodeOnEdge(c, t.connectionId, 'from');
    if (from?.type !== 'llm_task') return null;
    return {
      type: 'policy_rule_evaluation',
      score: 90,
      reason: 'Policy rule evaluation catches schema drift / policy violations before downstream nodes consume the LLM\'s response.',
    };
  },

  // 2. Before any writing connector_task, recommend policy_enforcement_gate.
  //    RC-AIPrompt-PhantomTypes-Round2 — was 'policy_gate', a short
  //    alias that confused both AI and operators. Use the canonical
  //    type name; runtime behaviour is identical.
  (t, c) => {
    if (t.kind !== 'edge') return null;
    const to = nodeOnEdge(c, t.connectionId, 'to');
    if (!isWritingConnectorTask(to)) return null;
    return {
      type: 'policy_enforcement_gate',
      score: 85,
      reason: 'Policy enforcement gate blocks disallowed data from leaving Agentlets via this writing connector.',
    };
  },

  // 3. Inside a branch with no exit, recommend terminate.
  (t, c) => {
    if (t.kind !== 'branch') return null;
    if (!branchHasNoExit(c, t.branchEdgeId)) return null;
    return {
      type: 'terminate',
      score: 80,
      reason: 'This branch currently has no exit — adding a terminate closes it cleanly so the runtime knows to stop here.',
    };
  },

  // 4. After every external connector_task, recommend audit_log.
  (t, c) => {
    if (t.kind !== 'edge') return null;
    const from = nodeOnEdge(c, t.connectionId, 'from');
    if (!from || from.type !== 'connector_task') return null;
    // We don't know "external" without connector metadata at this layer; treat
    // any connector_task as a candidate. Score is lower than the writing-
    // connector policy gate (#2) so it doesn't crowd it out when both fire.
    return {
      type: 'audit_record',
      score: 65,
      reason: 'Connector calls are best audited so compliance can trace what data left the platform and when.',
    };
  },

  // 5. After a denied branch of an approval_gate / human_task, recommend notify.
  (t, c) => {
    if (t.kind !== 'branch') return null;
    const sw = findNode(c, t.switchNodeId);
    if (!sw) return null;
    const isApprovalish = sw.type === 'approval_gate' || sw.type === 'human_task';
    const isDenied = (t.branchLabel || '').toLowerCase().includes('den');
    if (!isApprovalish || !isDenied) return null;
    return {
      type: 'notification_task',
      score: 75,
      reason: 'Tell the requester their approval was denied — the denied branch usually needs a courteous notification.',
    };
  },

  // 6. On the first edge after start, recommend http_task as the most common
  //    "fetch input" first step.
  (t, c) => {
    if (!isFirstEdgeAfterStart(c, t)) return null;
    return {
      type: 'http_task',
      score: 55,
      reason: 'Workflows usually start by fetching or accepting input — an HTTP request is the most common first step.',
    };
  },
];

/**
 * Run every rule against (target, canvas) and return at most `limit`
 * recommendations, ordered by score descending. Filters out duplicates by
 * type (rule conflicts pick the higher score).
 *
 * @param {InsertionTarget} target
 * @param {CanvasView} canvas
 * @param {number} [limit=4]
 * @returns {Hint[]}
 */
export function recommend(target, canvas, limit = 4) {
  if (!target || !canvas) return [];
  const byType = new Map();
  for (const rule of rules) {
    let hint = null;
    try { hint = rule(target, canvas); }
    catch (_) { /* defensive — never let a buggy rule break the picker */ }
    if (!hint || hint.score < 30) continue;
    const prior = byType.get(hint.type);
    if (!prior || prior.score < hint.score) byType.set(hint.type, hint);
  }
  return [...byType.values()]
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}
